import React from 'react';

/**
 * Vercel-style animated loader component
 * @param {Object} props
 * @param {string} props.size - Size of the loader ('sm', 'md', 'lg')
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.text - Optional loading text to display
 */
const Loader = ({ size = 'md', className = '', text = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-8 h-8 border-[3px]',
    lg: 'w-12 h-12 border-[3px]'
  };

  return (
    <div className={`flex flex-col items-center justify-center gap-3 ${className}`}>
      <div className="relative">
        {/* Vercel-style spinning loader */}
        <div
          className={`${sizeClasses[size]} border-gray-200 dark:border-gray-700 border-t-black dark:border-t-white rounded-full animate-spin`}
          style={{
            animationDuration: '0.6s'
          }}
        />
      </div>
      {text && (
        <p className="text-sm text-gray-600 dark:text-gray-400 animate-pulse">
          {text}
        </p>
      )}
    </div>
  );
};

/**
 * Full page loader with overlay
 */
export const PageLoader = ({ text = 'Loading...' }) => {
  return (
    <div className="fixed inset-0 bg-white bg-opacity-90 backdrop-blur-sm flex items-center justify-center z-50">
      <Loader size="lg" text={text} />
    </div>
  );
};

/**
 * Inline loader for content sections
 */
export const InlineLoader = ({ text = '' }) => {
  return (
    <div className="w-full py-8 flex items-center justify-center">
      <Loader size="md" text={text} />
    </div>
  );
};

/**
 * Button loader (small spinner for buttons)
 */
export const ButtonLoader = () => {
  return (
    <div
      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
      style={{
        animationDuration: '0.6s'
      }}
    />
  );
};

/**
 * Skeleton loader for content placeholders
 */
export const SkeletonLoader = ({ className = '', count = 1 }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className={`animate-pulse bg-gray-100 dark:bg-gray-800 rounded ${className}`}
          style={{
            animationDuration: '2s'
          }}
        />
      ))}
    </>
  );
};

export default Loader;
